import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';

const bgDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark community courtyard',
    },
  },
};

interface PageHeroProps {
  tokenPrefix?: string;
  title?: string;
  eyebrow?: string;
}

export function PageHero({ tokenPrefix, title, eyebrow }: PageHeroProps) {
  const prefix = tokenPrefix || 'pagehero';
  const titlePath = `${prefix}.title`;
  const eyebrowPath = `${prefix}.eyebrow`;
  const bgPath = `${prefix}.background`;

  const bg = useSlotGroup(bgPath, bgDef);

  return (
    <section className="relative flex min-h-[684px] w-full flex-col justify-end overflow-clip bg-[rgb(var(--ala-dark))]">
      <EditableSlotGroup groupPath={bgPath} definition={bgDef}>
        <div className="absolute inset-0 z-0">
          <VibesitesImage
            url={
              bg.image ||
              ''
            }
            alt={bg.alt || ''}
            priority
            sizes="100vw"
            className="h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(91.65deg, rgb(var(--ala-primary)) 0%, rgb(var(--ala-primary)/0) 100%)',
            }}
          />
        </div>
      </EditableSlotGroup>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative z-10 flex flex-col gap-4 p-6 md:pb-[clamp(22px,6vw,87px)] md:pl-[clamp(16px,6vw,87px)]"
      >
        <div className="flex items-center gap-3">
          <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
          <RichTextContent
            path={eyebrowPath}
            label="Eyebrow"
            as="span"
            defaultValue={`<p>${eyebrow || 'american landmark'}</p>`}
            className="font-heading text-base font-bold uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-on-primary))]"
          />
        </div>
        <RichTextContent
          path={titlePath}
          label="Title"
          as="h1"
          defaultValue={`<p>${title || 'Page'}</p>`}
          className="font-heading text-[clamp(38px,4.86vw,70px)] font-bold leading-[1.15] text-[rgb(var(--ala-on-primary))]"
        />
      </motion.div>

      {/* Schema-lint anchors for canonical keys */}
      <RichTextContent
        path="pagehero.eyebrow"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="pagehero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="pagehero.background"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Page Hero',
  tokens: {
    'pagehero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>american landmark</p>',
    },
    'pagehero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
  },
  slotGroups: {
    'pagehero.background': bgDef,
  },
};

(PageHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageHero;
